import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useLanguage } from "@/contexts/LanguageContext";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { ShoppingCart, CreditCard, MapPin, Package, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatBHD } from "@/lib/utils";
import { products } from "@/data/products";
import { supabase } from "@/integrations/supabase/client";

const Checkout = () => {
  const { user } = useAuth();
  const { cart, cartCount, clearCart } = useCart();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");

  // Form state
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [governorate, setGovernorate] = useState("Capital");
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [notes, setNotes] = useState("");

  // Redirect if cart is empty
  useEffect(() => {
    if (cartCount === 0 && !orderPlaced) {
      toast.error("Your cart is empty");
      navigate("/shop");
    }
  }, [cartCount, orderPlaced, navigate]);

  // Pre-fill user info
  useEffect(() => {
    if (user) {
      setEmail(user.email || "");
      // Load profile to get name and phone
      loadUserProfile();
    }
  }, [user]);

  const loadUserProfile = async () => {
    if (!user) return;

    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", user.id)
      .single();

    if (data) {
      setFullName(data.full_name || "");
      setPhone(data.phone || "");
    }
  };

  // Get cart with product details
  const cartWithDetails = cart.map((item) => {
    const product = products.find((p) => p.id === item.product_id);
    return { ...item, product };
  }).filter((item) => item.product);

  // Calculate totals
  const subtotal = cartWithDetails.reduce((sum, item) => {
    return sum + (item.product?.price || 0) * item.quantity;
  }, 0);

  const shipping = subtotal >= 20 ? 0 : 2.5;
  const tax = 0; // Bahrain has no VAT currently
  const total = subtotal + shipping + tax;

  const governorates = [
    "Capital",
    "Muharraq",
    "Northern",
    "Southern",
  ];

  const handlePlaceOrder = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!fullName || !email || !phone || !addressLine1 || !city) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);

    try {
      // Prepare order data
      const orderData = {
        user_id: user?.id || null,
        status: "pending",
        items: cartWithDetails.map(item => ({
          product_id: item.product_id,
          name: item.product?.name,
          quantity: item.quantity,
          price: item.product?.price,
          size: item.size,
          color: item.color,
          custom_design: item.custom_design,
        })),
        subtotal,
        shipping_cost: shipping,
        tax: tax,
        discount: 0,
        total_amount: total,
        shipping_address: {
          full_name: fullName,
          phone,
          address_line1: addressLine1,
          address_line2: addressLine2,
          city,
          governorate,
          country: "Bahrain",
        },
        payment_method: paymentMethod,
        payment_status: paymentMethod === "cod" ? "pending" : "pending",
        customer_notes: notes || null,
      };

      // Insert order
      const { data: order, error } = await supabase
        .from("orders")
        .insert(orderData)
        .select()
        .single();

      if (error) throw error;

      // Clear cart
      await clearCart();

      // Show success
      setOrderNumber(order.order_number);
      setOrderPlaced(true);
      toast.success("Order placed successfully!");

      // Scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Error placing order:", error);
      toast.error("Failed to place order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Order Success Screen
  if (orderPlaced) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-1 flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 py-12">
          <Card className="max-w-2xl w-full mx-4">
            <CardContent className="pt-6">
              <div className="text-center space-y-6">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>

                <div>
                  <h1 className="text-3xl font-bold mb-2">Order Placed Successfully!</h1>
                  <p className="text-muted-foreground">
                    Thank you for your order. We'll send you a confirmation email shortly.
                  </p>
                </div>

                <div className="bg-muted/50 rounded-lg p-6">
                  <p className="text-sm text-muted-foreground mb-2">Order Number</p>
                  <p className="text-2xl font-bold text-primary">{orderNumber}</p>
                </div>

                <Separator />

                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Total</p>
                      <p className="text-lg font-bold">{formatBHD(total)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Items</p>
                      <p className="text-lg font-bold">{cartCount}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Payment</p>
                      <p className="text-lg font-bold">COD</p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="flex gap-4">
                  <Button className="flex-1" onClick={() => navigate("/profile")}>
                    View Orders
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => navigate("/")}>
                    Continue Shopping
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      <main className="flex-1 bg-gradient-to-br from-background via-background to-muted/20">
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-cyan via-brand-magenta to-brand-yellow py-12">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-center gap-3">
              <ShoppingCart className="h-8 w-8 text-white" />
              <h1 className="text-3xl md:text-4xl font-bold text-white">Checkout</h1>
            </div>
          </div>
        </div>

        {/* Checkout Form */}
        <div className="container mx-auto px-4 py-12">
          <form onSubmit={handlePlaceOrder}>
            <div className="grid gap-8 lg:grid-cols-3">
              {/* Left Column - Forms */}
              <div className="lg:col-span-2 space-y-6">
                {/* Contact Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Contact Information
                    </CardTitle>
                    <CardDescription>How can we reach you?</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="fullName">Full Name *</Label>
                        <Input
                          id="fullName"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+973-12345678"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        required
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Shipping Address */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-5 w-5" />
                      Shipping Address
                    </CardTitle>
                    <CardDescription>Where should we deliver your order?</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="address1">Address Line 1 *</Label>
                      <Input
                        id="address1"
                        value={addressLine1}
                        onChange={(e) => setAddressLine1(e.target.value)}
                        placeholder="Building, Flat number, Street"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address2">Address Line 2 (Optional)</Label>
                      <Input
                        id="address2"
                        value={addressLine2}
                        onChange={(e) => setAddressLine2(e.target.value)}
                        placeholder="Landmark, Area"
                      />
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="governorate">Governorate *</Label>
                        <select
                          id="governorate"
                          value={governorate}
                          onChange={(e) => setGovernorate(e.target.value)}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          required
                        >
                          {governorates.map((gov) => (
                            <option key={gov} value={gov}>
                              {gov}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Payment Method */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      Payment Method
                    </CardTitle>
                    <CardDescription>How would you like to pay?</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                      <div className="flex items-center space-x-3 border rounded-lg p-4">
                        <RadioGroupItem value="cod" id="cod" />
                        <Label htmlFor="cod" className="flex-1 cursor-pointer">
                          <div className="font-medium">Cash on Delivery</div>
                          <div className="text-sm text-muted-foreground">
                            Pay when you receive your order
                          </div>
                        </Label>
                      </div>
                    </RadioGroup>

                    <div className="mt-4 space-y-2">
                      <Label htmlFor="notes">Order Notes (Optional)</Label>
                      <Textarea
                        id="notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Any special instructions for your order?"
                        rows={3}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Order Summary */}
              <div className="lg:col-span-1">
                <Card className="sticky top-4">
                  <CardHeader>
                    <CardTitle>Order Summary</CardTitle>
                    <CardDescription>{cartCount} items</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Cart Items */}
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {cartWithDetails.map((item) => (
                        <div key={`${item.product_id}-${item.size}-${item.color}`} className="flex gap-3">
                          <div className="h-16 w-16 rounded-md overflow-hidden bg-muted flex-shrink-0">
                            <img
                              src={item.product?.image}
                              alt={item.product?.name}
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium line-clamp-2">
                              {item.product?.name}
                            </p>
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-xs text-muted-foreground">
                                Qty: {item.quantity}
                              </span>
                              <span className="text-sm font-bold">
                                {formatBHD((item.product?.price || 0) * item.quantity)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <Separator />

                    {/* Totals */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span className="font-medium">{formatBHD(subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Shipping</span>
                        <span className="font-medium">
                          {shipping === 0 ? (
                            <span className="text-green-600">FREE</span>
                          ) : (
                            formatBHD(shipping)
                          )}
                        </span>
                      </div>
                      {tax > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Tax</span>
                          <span className="font-medium">{formatBHD(tax)}</span>
                        </div>
                      )}
                      <Separator />
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total</span>
                        <span className="text-primary">{formatBHD(total)}</span>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-brand-cyan via-brand-magenta to-brand-yellow text-white"
                      size="lg"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Placing Order...
                        </>
                      ) : (
                        "Place Order"
                      )}
                    </Button>

                    <p className="text-xs text-center text-muted-foreground">
                      By placing your order, you agree to our Terms & Conditions
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Checkout;